// Lazy, cached preview thumbnail for a Visual (V), Dashboard (D) or Smart
// Page (S) card in the Analytics workbench list. It reuses the SAME render +
// data path as the live preview (InsightVisual + /api/visual-data for V/D;
// SmartPageRuntime for S), with two cost controls:
//   • LAZY  — an IntersectionObserver holds the (expensive, live-DB) data fetch
//     until the card actually scrolls into view.
//   • CACHE — the resolved dataset is memoized in a module-level Map keyed by
//     project::kind::objName, so re-mounts (scroll away/back, list re-render,
//     returning from the editor) render INSTANTLY with no re-execution.
// The rendered chart is static: no onSelect / onShowUnderlying / toolbar, and
// pointer events are disabled so the whole card stays a single click target.
import { useEffect, useMemo, useRef, useState } from 'react'
import { InsightVisual, dashGridContainerStyle, dashTileStyle, dashGridHeightPx } from '@twasta/visual-engine'
import type { VisualDataPayload } from '@twasta/visual-engine'
import { ThemedPageShell } from '@twasta/page-runtime'
import {
  fetchVisualData,
  fetchDashboardData,
  type DashboardDataResult,
} from '@/api/visualData'
import { loadPageObject } from '@/api/designers'
import { toV2, normalizePageKind } from '@/pages/designers/page/migrate'
import { resolveTheme } from '@/pages/designers/page/theme'
import type { PageModel } from '@/pages/designers/page/types'
import SmartPageRuntime from '@/pages/workbench/smartpage-preview/SmartPageRuntime'

export type PreviewKind = 'V' | 'D' | 'S'

type Resolved =
  | { status: 'ok'; kind: 'V'; payload: VisualDataPayload }
  | { status: 'ok'; kind: 'D'; dashboard: DashboardDataResult }
  | { status: 'ok'; kind: 'S'; page: PageModel }
  | { status: 'needsInput' }
  | { status: 'empty' }
  | { status: 'error'; message: string }

const cacheKey = (project: string, kind: PreviewKind, objName: string) =>
  `${project}::${kind}::${objName}`

// Resolved datasets (memoized) + in-flight promises (de-duped concurrent loads).
const CACHE = new Map<string, Resolved>()
const INFLIGHT = new Map<string, Promise<Resolved>>()

/** Drop cached preview(s) so the next view re-executes (e.g. after an edit). */
export function bustPreview(project: string, kind: PreviewKind, objName: string) {
  const k = cacheKey(project, kind, objName)
  CACHE.delete(k)
  INFLIGHT.delete(k)
}

function rowCountOf(payload: VisualDataPayload): number {
  if (typeof payload.rowCount === 'number') return payload.rowCount
  // jsonData is [schemaRow, ...dataRows]; anything beyond the schema row is data.
  return Array.isArray(payload.jsonData) ? Math.max(0, payload.jsonData.length - 1) : 0
}

async function loadResolved(project: string, kind: PreviewKind, objName: string): Promise<Resolved> {
  const k = cacheKey(project, kind, objName)
  const cached = CACHE.get(k)
  if (cached) return cached
  const existing = INFLIGHT.get(k)
  if (existing) return existing

  const p = (async (): Promise<Resolved> => {
    try {
      if (kind === 'V') {
        const payload = await fetchVisualData({ project, obj_name: objName })
        if (payload.needsInput) return { status: 'needsInput' }
        if (rowCountOf(payload) === 0) return { status: 'empty' }
        return { status: 'ok', kind: 'V', payload }
      }
      if (kind === 'S') {
        // Same load + normalize path as the page designer's PreviewModal;
        // no run=true — the thumbnail never executes the before-load gate.
        const data = await loadPageObject(project, objName)
        const root = (data?.content ?? data) as { page?: PageModel } | PageModel | null
        const raw = (root && typeof root === 'object' && 'page' in root && root.page
          ? root.page
          : root) as PageModel | null
        if (!raw || typeof raw !== 'object' || !raw.obj_name) return { status: 'empty' }
        return { status: 'ok', kind: 'S', page: normalizePageKind(toV2(raw)) }
      }
      const dashboard = await fetchDashboardData({ project, obj_name: objName })
      const hasTile = (dashboard.tiles || []).some(
        (t) => Array.isArray(t.jsonData) && t.jsonData.length > 1,
      )
      if (!hasTile) return { status: 'empty' }
      return { status: 'ok', kind: 'D', dashboard }
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      return { status: 'error', message: detail || 'Preview unavailable' }
    }
  })()

  INFLIGHT.set(k, p)
  const resolved = await p
  INFLIGHT.delete(k)
  CACHE.set(k, resolved)
  return resolved
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, color: 'var(--color-text-secondary)', textAlign: 'center', padding: 8,
      }}
    >
      {children}
    </div>
  )
}

// Scaled, read-only tile grid — the whole dashboard laid out, then uniformly
// scaled to fill the thumbnail (width) and fit its height.
function DashboardMini({ dashboard, height }: { dashboard: DashboardDataResult; height: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    setWidth(el.clientWidth)
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const tiles = (dashboard.tiles || []).filter((t) => Array.isArray(t.jsonData) && t.jsonData.length > 1)
  const maxRowUnits = tiles.reduce((m, t) => {
    const y = Number(t.POSITION?.Y ?? 0)
    const h = Number(t.POSITION?.HEIGHT ?? 4)
    return Math.max(m, y + h)
  }, 0) || 4

  const ROW_PX = 90
  const GAP = 8
  const PAD = 8
  const naturalH = dashGridHeightPx(maxRowUnits, ROW_PX, GAP, PAD)
  // Uniform scale to fit the natural height into the thumbnail. Inner width is
  // inverse-scaled so the grid still fills the card width after scaling.
  const scale = width > 0 ? Math.min(1, height / naturalH) : 1
  const innerW = scale > 0 ? width / scale : width

  return (
    <div ref={ref} style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: 'var(--color-bg)' }}>
      {width > 0 && (
        <div
          style={{
            width: innerW,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            pointerEvents: 'none',
          }}
        >
          <div style={{ ...dashGridContainerStyle(ROW_PX, GAP, PAD, { stretch: false }), width: innerW }}>
            {tiles.map((t, i) => (
              <div
                key={t.VISUAL_ID || i}
                style={{
                  ...dashTileStyle(t.POSITION, GAP),
                  overflow: 'hidden',
                  borderRadius: 8,
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg-secondary)',
                }}
              >
                {t.jsonData && t.visualLayout ? (
                  <InsightVisual
                    jsonData={t.jsonData}
                    visualLayout={t.visualLayout}
                    optionsArr={t.optionsArr}
                    style={{ height: '100%', width: '100%' }}
                  />
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Scaled, read-only smart-page render — the page laid out at a fixed desktop
// width with the real runtime (same composition as the page PreviewModal),
// then uniformly scaled to the thumbnail width; the visible strip is the top
// of the page, like a website screenshot.
const PAGE_DESKTOP_W = 1100

function PageMini({ page, project, height }: { page: PageModel; project: string; height: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    setWidth(el.clientWidth)
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const theme = useMemo(() => resolveTheme(page.theme), [page.theme])
  const scale = width > 0 ? width / PAGE_DESKTOP_W : 1

  return (
    <div ref={ref} style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: 'var(--color-bg)' }}>
      {width > 0 && (
        <div
          style={{
            width: PAGE_DESKTOP_W,
            height: height / scale,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          <ThemedPageShell theme={theme} minHeight="100%">
            <SmartPageRuntime page={page} mode="preview" docId={null} project={project} />
          </ThemedPageShell>
        </div>
      )}
    </div>
  )
}

export default function PreviewThumbnail({
  project, objName, kind, height = 132,
}: {
  project: string
  objName: string
  kind: PreviewKind
  height?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [state, setState] = useState<Resolved | { status: 'loading' }>(() => {
    const cached = CACHE.get(cacheKey(project, kind, objName))
    return cached || { status: 'loading' }
  })

  // Reset to the cached (or loading) state whenever the target changes.
  useEffect(() => {
    const cached = CACHE.get(cacheKey(project, kind, objName))
    setState(cached || { status: 'loading' })
  }, [project, kind, objName])

  // Lazy: only fetch once the card scrolls into view.
  useEffect(() => {
    const el = ref.current
    if (!el || visible) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true)
        io.disconnect()
      }
    }, { rootMargin: '200px' })
    io.observe(el)
    return () => io.disconnect()
  }, [visible])

  useEffect(() => {
    if (!visible) return
    if (state.status !== 'loading') return // already have a cached/resolved value
    let alive = true
    void loadResolved(project, kind, objName).then((r) => {
      if (alive) setState(r)
    })
    return () => { alive = false }
  }, [visible, project, kind, objName, state.status])

  let body: React.ReactNode
  if (state.status === 'loading') {
    body = (
      <Centered>
        <span className="spinner" style={{ width: 16, height: 16 }} />
      </Centered>
    )
  } else if (state.status === 'error') {
    body = <Centered>{state.message}</Centered>
  } else if (state.status === 'needsInput') {
    body = <Centered>Needs input to preview</Centered>
  } else if (state.status === 'empty') {
    body = <Centered>No data to preview</Centered>
  } else if (state.kind === 'V') {
    body = (
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', background: 'var(--color-bg)' }}>
        <InsightVisual
          jsonData={state.payload.jsonData}
          visualLayout={state.payload.visualLayout}
          optionsArr={state.payload.optionsArr}
          style={{ height: '100%', width: '100%' }}
        />
      </div>
    )
  } else if (state.kind === 'S') {
    body = <PageMini page={state.page} project={project} height={height} />
  } else {
    body = <DashboardMini dashboard={state.dashboard} height={height} />
  }

  return (
    <div ref={ref} style={{ position: 'absolute', inset: 0 }}>
      {body}
    </div>
  )
}
